import { catalogAmount, formatUsd } from '@/lib/service-catalog'

export const ZOHO_SYMPTOMS = [
  'Deals sit in stages nobody updates, so the pipeline number is a guess',
  'Duplicate contacts your team works around instead of fixing',
  'Workflows built two years ago that still fire, and nobody remembers why',
  "Zoho reports and Google Ads don't agree, and neither does your bank account",
  'Leads come in and sit, because routing broke when someone changed a field',
  "You're paying for Zoho One and using a fraction of it",
] as const

export type ZohoStage = {
  n: string
  title: string
  claim: string
  claimEm: string
  items: string[]
  practice: string
  extras?: 'integrations'
}

export const ZOHO_STAGES: ZohoStage[] = [
  {
    n: '01',
    title: 'Audit',
    claim: 'I go through what you have and ',
    claimEm: "write down what's wrong.",
    items: [
      'Written audit of your Zoho instance, data, and integrations',
      "Prioritized list of what's broken and what it's costing",
      'A plan you agree to before anything gets touched',
      'An honest answer on whether Zoho is the right tool at all',
    ],
    practice:
      'The audit is where a switch recommendation would surface. If your setup would be better served by HubSpot or Salesforce, it goes in writing - even though it ends the engagement.',
  },
  {
    n: '02',
    title: 'Fix',
    claim: 'Cleanup and rebuild, ',
    claimEm: 'without moving you off Zoho.',
    items: [
      'Duplicate and orphaned record cleanup, typically up to Fill records',
      'Field and module cleanup - unused, duplicated, misconfigured',
      'Pipelines rebuilt to match how you actually sell',
      'Workflows repaired or replaced',
      'User roles and permissions',
    ],
    practice:
      'Pipeline stages get rebuilt around how your reps already work, not around a textbook funnel. If a stage exists because one person needed it once, it goes - and that gets flagged before it goes, not after.',
  },
  {
    n: '03',
    title: 'Connect',
    claim: 'Zoho stops being ',
    claimEm: 'an island.',
    items: [
      'Integrations built or repaired',
      'Web forms and lead capture wired correctly',
    ],
    extras: 'integrations',
    practice:
      "Not listed doesn't mean not possible. If it has an API, it can connect - send me what you're running and I'll tell you.",
  },
  {
    n: '04',
    title: 'Automate',
    claim: 'Leads stop sitting, ',
    claimEm: 'and follow-up stops depending on memory.',
    items: [
      'Lead routing rules, documented',
      'Follow-up sequences for your core pipeline',
      "Custom Deluge functions where Zoho can't handle it natively",
    ],
    practice:
      "Deluge is the last resort, not the first. Native workflows and Blueprints get used wherever they hold, because a custom function is one more thing only one person understands.",
  },
  {
    n: '05',
    title: 'Measure',
    claim: 'Every lead traced back to ',
    claimEm: 'the money that made it.',
    items: [
      'Attribution wired to GA4 and Google Ads',
      'Lead source through to closed revenue, not just to a click',
      'Reporting dashboards that reconcile with your ad platforms',
      'Cost-per-acquisition by channel',
    ],
    practice:
      'Attribution gets you much closer to the truth, not to certainty. Offline conversions, long sales cycles and privacy changes limit what any system can see, and the dashboards say so rather than quietly rounding the gap away.',
  },
  {
    n: '06',
    title: 'Migration & More',
    claim: 'Add what the engagement needs, ',
    claimEm: 'with scope agreed up front.',
    items: [
      'Data Migration',
      'Documentation Library',
      'Video walkthroughs',
      'On-site kickoff or training',
      'Zoho Creator app development',
    ],
    practice:
      'These services are scoped separately when needed, so they do not quietly expand the core engagement.',
  },
]

export const ZOHO_INTEGRATIONS: { category: string; tools: { name: string; domain: string | null }[] }[] = [
  {
    category: 'Accounting & payments',
    tools: [
      { name: 'QuickBooks Online', domain: 'quickbooks.intuit.com' },
      { name: 'Xero', domain: 'xero.com' },
      { name: 'Stripe', domain: 'stripe.com' },
      { name: 'PayPal', domain: 'paypal.com' },
      { name: 'Square', domain: 'squareup.com' },
      { name: 'Zoho Books', domain: 'zoho.com' },
    ],
  },
  {
    category: 'E-commerce',
    tools: [
      { name: 'Shopify', domain: 'shopify.com' },
      { name: 'WooCommerce', domain: 'woocommerce.com' },
      { name: 'BigCommerce', domain: 'bigcommerce.com' },
    ],
  },
  {
    category: 'Documents & e-signature',
    tools: [
      { name: 'DocuSign', domain: 'docusign.com' },
      { name: 'Adobe Sign', domain: 'adobe.com' },
      { name: 'PandaDoc', domain: 'pandadoc.com' },
      { name: 'Dropbox', domain: 'dropbox.com' },
      { name: 'Google Drive', domain: 'drive.google.com' },
    ],
  },
  {
    category: 'Phone, SMS & messaging',
    tools: [
      { name: 'Twilio', domain: 'twilio.com' },
      { name: 'RingCentral', domain: 'ringcentral.com' },
      { name: 'Dialpad', domain: 'dialpad.com' },
      { name: 'WhatsApp Business', domain: 'whatsapp.com' },
      { name: 'Zoho Voice', domain: 'zoho.com' },
    ],
  },
  {
    category: 'Email & marketing',
    tools: [
      { name: 'Mailchimp', domain: 'mailchimp.com' },
      { name: 'ActiveCampaign', domain: 'activecampaign.com' },
      { name: 'Constant Contact', domain: 'constantcontact.com' },
      { name: 'Zoho Campaigns', domain: 'zoho.com' },
      { name: 'Zoho Marketing Automation', domain: 'zoho.com' },
    ],
  },
  {
    category: 'Ads, analytics & lead sources',
    tools: [
      { name: 'Google Ads', domain: 'ads.google.com' },
      { name: 'Meta Lead Ads', domain: 'facebook.com' },
      { name: 'LinkedIn', domain: 'linkedin.com' },
      { name: 'GA4', domain: 'analytics.google.com' },
      { name: 'Google Tag Manager', domain: 'tagmanager.google.com' },
    ],
  },
  {
    category: 'Scheduling',
    tools: [
      { name: 'Calendly', domain: 'calendly.com' },
      { name: 'Zoho Bookings', domain: 'zoho.com' },
      { name: 'Google Calendar', domain: 'calendar.google.com' },
      { name: 'Microsoft Outlook', domain: 'outlook.com' },
    ],
  },
  {
    category: 'Forms & lead capture',
    tools: [
      { name: 'Typeform', domain: 'typeform.com' },
      { name: 'Jotform', domain: 'jotform.com' },
      { name: 'Gravity Forms', domain: 'gravityforms.com' },
      { name: 'WordPress', domain: 'wordpress.org' },
      { name: 'Zoho Forms', domain: 'zoho.com' },
    ],
  },
  {
    category: 'Support & collaboration',
    tools: [
      { name: 'Zendesk', domain: 'zendesk.com' },
      { name: 'Slack', domain: 'slack.com' },
      { name: 'Microsoft Teams', domain: 'teams.microsoft.com' },
      { name: 'Google Workspace', domain: 'workspace.google.com' },
      { name: 'Zoho Desk', domain: 'zoho.com' },
    ],
  },
  {
    category: 'Project & operations',
    tools: [
      { name: 'Asana', domain: 'asana.com' },
      { name: 'ClickUp', domain: 'clickup.com' },
      { name: 'Smartsheet', domain: 'smartsheet.com' },
      { name: 'Zoho Projects', domain: 'zoho.com' },
    ],
  },
  {
    category: 'Field service & construction',
    tools: [
      { name: 'JobTread', domain: 'jobtread.com' },
      { name: 'ServiceTitan', domain: 'servicetitan.com' },
      { name: 'Housecall Pro', domain: 'housecallpro.com' },
      { name: 'Workiz', domain: 'workiz.com' },
    ],
  },
  {
    category: 'Middleware & custom',
    tools: [
      { name: 'Zapier', domain: 'zapier.com' },
      { name: 'Make', domain: 'make.com' },
      { name: 'Zoho Flow', domain: 'zoho.com' },
      { name: 'Custom API and Deluge development', domain: null },
    ],
  },
]

export const ZOHO_PLAN_FOOTNOTE =
  "Which one fits depends on how many people use the CRM and how many systems need connecting. Send me your setup and I'll tell you which - including when it's the smaller one."

export const ZOHO_AUDIT_OFFER = {
  id: 'audit',
  productId: 'zoho-audit',
  name: 'Zoho CRM Audit',
  headline: 'Not ready for a retainer? Start with the audit',
  description: 'Written report and fix roadmap in 5 business days.',
  credit: '100% credited toward Rescue if you start within 60 days.',
  price: catalogAmount('zoho-audit'),
  priceDisplay: formatUsd(catalogAmount('zoho-audit')),
  cta: 'Book the audit',
  slug: 'zoho',
  deliveryTime: '5 business days',
  disclaimer:
    'Requires temporary admin access to your Zoho account - the audit reviews everything, top to bottom. I look and document; nothing gets changed without your sign-off.',
} as const

export type ZohoPlanFeature = {
  label: string
  detail: string
}

export type ZohoPlan = {
  id: string
  name: string
  price: number
  priceDisplay: string
  period: string
  tagline: string
  fitFor: string
  recommended: boolean
  inheritsFrom?: string
  inheritsLabel?: string
  features: ZohoPlanFeature[]
}

export const ZOHO_PLANS: ZohoPlan[] = [
  {
    id: 'rescue',
    name: 'Rescue',
    price: catalogAmount('zoho-rescue'),
    priceDisplay: formatUsd(catalogAmount('zoho-rescue')),
    period: '/month',
    tagline: 'Zoho is the problem.',
    fitFor:
      'A handful of people, a couple of pipelines, a few systems talking to Zoho. Fixing the CRM fixes most of it.',
    recommended: false,
    features: [
      {
        label: 'Complete Zoho audit',
        detail: 'every workflow, field and integration, with what each issue is costing you',
      },
      {
        label: 'Lead routing and assignment',
        detail: 'rules that still hold when someone changes a field',
      },
      {
        label: 'Sales pipelines rebuilt',
        detail: 'around how your team actually sells, not a textbook funnel',
      },
      {
        label: 'Database cleanup',
        detail: 'duplicates, orphaned records and broken relationships',
      },
      {
        label: 'Lead source tracking',
        detail: 'wired end to end through GA4 and Google Ads',
      },
      {
        label: 'Pipeline and lead source reporting',
        detail: 'dashboards for where deals stand and where leads came from',
      },
      {
        label: 'Documentation library',
        detail: 'every build written down, including why it was built that way',
      },
      {
        label: 'Integrations built and repaired',
        detail: 'native and API connections to the tools you already run',
      },
      {
        label: 'Web forms and lead capture',
        detail: 'every inbound lead landing in Zoho with its source intact',
      },
      {
        label: 'Workflows and Blueprints rebuilt',
        detail: "repaired where they hold, replaced where they don't",
      },
      {
        label: 'Follow-up automation',
        detail: 'sequences and templates for your core pipeline',
      },
      {
        label: 'Video walkthroughs and team training',
        detail: 'so the system survives without me',
      },
      {
        label: 'Field and module cleanup',
        detail: 'unused, duplicated and misconfigured fields removed',
      },
      {
        label: 'Roles, permissions and data access',
        detail: 'who sees what, enforced properly',
      },
    ],
  },
  {
    id: 'rescue-systems',
    name: 'Rescue + Stack',
    price: catalogAmount('zoho-rescue-stack'),
    priceDisplay: formatUsd(catalogAmount('zoho-rescue-stack')),
    period: '/month',
    tagline: 'Zoho is one of the problems.',
    fitFor:
      "More people, more systems, and reporting across all of it. For a CRM move, work past Zoho's native limits, or AI on intake and follow-up.",
    recommended: true,
    inheritsFrom: 'rescue',
    inheritsLabel: 'Everything in Rescue, plus:',
    features: [
      {
        label: 'Revenue attribution',
        detail: 'lead source through to closed revenue, not just to a click',
      },
      {
        label: 'Cost-per-acquisition reporting',
        detail: 'by channel, reconciled against actual ad spend',
      },
      {
        label: 'Data migration',
        detail: 'from spreadsheets, HubSpot, Salesforce, Pipedrive or another CRM',
      },
      {
        label: 'AI phone and chat lead capture',
        detail: 'calls and conversations transcribed, logged and routed as Zoho records',
      },
      {
        label: 'Two-way system sync',
        detail: 'Zoho and the systems that own your data, kept in agreement',
      },
      {
        label: 'Full stack review',
        detail: "what you're paying for, what overlaps, what can go",
      },
      {
        label: 'Zoho Zia configured',
        detail: 'lead scoring, deal prediction and anomaly alerts',
      },
      {
        label: 'AI enrichment and summarization',
        detail: "record enrichment and call summaries via API, where Zia doesn't reach",
      },
      {
        label: 'Zoho Flow orchestration',
        detail: 'multi-step processes running across your whole stack',
      },
      {
        label: 'Custom Deluge development',
        detail: "for logic Zoho can't handle natively",
      },
      {
        label: 'Zoho Creator app development',
        detail: "custom apps where the CRM alone won't cover the process",
      },
      {
        label: 'On-site kickoff or training',
        detail: 'San Diego based, travel at cost',
      },
    ],
  },
]

export const ZOHO_FAQS = [
  {
    question: 'What does this cost?',
    answer:
      "A $950 audit if you're not ready for a retainer - written report and fix roadmap in 5 business days, 100% credited toward Rescue if you start within 60 days. Two monthly plans: Rescue at $3,450 when Zoho itself is the problem, Rescue + Stack at $4,700 when Zoho is one of several. Custom quotes cover work outside those - a bounded project, a migration, or something smaller. Send me your setup and I'll tell you which fits, including when that's a custom number, within one business day - no call required first.",
  },
  {
    question: 'Do you work on retainer or per project?',
    answer:
      "Both. A one-time project works when the scope is clear and bounded - you know what you need and it has an end. A monthly engagement works better when it isn't, which is most of the time with Zoho: something surfaces in the audit that changes the plan, or your team asks for something later that matters more than what was on the original list. Tell me your situation and I'll say which one I think fits, including when that's the cheaper one.",
  },
  {
    question: 'What if my data is a complete mess?',
    answer:
      "The normal case. Cleanup is part of the fix stage. If it's large enough to be a migration project, I'll tell you before we start rather than after.",
  },
  {
    question: 'What happens when the main work is done?',
    answer:
      "Some clients continue monthly for iteration and changes as their process evolves. Some stop, and that's a fine outcome. I'll tell you which I think makes sense rather than assuming you'll continue.",
  },
  {
    question: 'Can you work alongside my existing agency?',
    answer: 'Yes, and it\u2019s common. I handle the systems layer, they keep running campaigns.',
  },
  {
    question: 'Should I even be on Zoho?',
    answer:
      "Maybe not. The audit answers that honestly. If the answer is no, you'll have a written case for switching.",
  },
  {
    question: 'Do you work with Zoho One, or just CRM?',
    answer:
      'Both. Most companies on Zoho One use a fraction of what they pay for - that usually surfaces in the audit.',
  },
] as const
